import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useRealtime } from '../../contexts/RealtimeContext';
import { isCoachingAdmin } from '../../utils/rbac';
import { handleError } from '../../utils/errorHandler';

const CoachingProtectedRoute = ({ children }) => {
  const { currentUser, isInitialized, userProfile } = useRealtime();
  const location = useLocation();

  // Show loading while checking authentication
  if (!isInitialized || (currentUser && !userProfile)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // If no user is authenticated, redirect to login
  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user is a coaching admin or staff using proper RBAC
  let userIsCoachingAdmin = false;
  try {
    userIsCoachingAdmin = isCoachingAdmin(userProfile, currentUser) || userProfile?.role === 'coaching_staff';
  } catch (error) {
    handleError(error, {
      context: { userId: currentUser.uid, path: location.pathname },
      logError: true
    });
  }

  if (!userIsCoachingAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // Check if subscription is active (allow test payments)
  const isTestPayment = userProfile?.subscription?.isTestPayment === true;
  const isSubscriptionActive = userProfile?.subscription?.status === 'active' || userProfile?.isActive === true;
  const isDevelopment = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';
  
  // Allow access if: subscription is active, test payment, or in development mode
  if (!isSubscriptionActive && !isTestPayment && !isDevelopment) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 max-w-md mx-4 text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Subscription Required
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Your subscription is not active. Please complete the payment to access the coaching admin panel.
          </p>
          <button
            onClick={() => window.location.href = '/coaching-signup'}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Complete Payment
          </button>
        </div>
      </div>
    );
  }

  return children;
};

export default CoachingProtectedRoute;

